import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { Role } from "@/types/role.enum";

type RoleValue = (typeof Role)[keyof typeof Role];

/**
 * Returns whether the current user has at least one of the specified roles.
 *
 * Comparison is case-insensitive — the user's role is normalized to uppercase
 * before matching against the provided `allowedRoles` list.
 *
 * @param allowedRoles - One or more {@link Role} values to check against.
 * @returns `true` when the user's role matches any of the allowed roles, `false` otherwise.
 */
export function useIsRole(allowedRoles: RoleValue[]): boolean {
  const { user } = useSelector(selectCurrentAuth);

  return useMemo(
    () => allowedRoles.some((role) => user?.role.toUpperCase() === role),
    [user?.role, allowedRoles]
  );
}
